// Criteria × versions table with deltas vs the previous version — the "did the
// last publish actually improve quality" view.

import type { VersionSummary } from "../../services/evals";
import { scoreTone } from "./RubricSummary";

const LOW_SAMPLE = 20;

function pct(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `${Math.round(value * 100)}%`;
}

function Delta({ current, previous }: { current: number | null; previous: number | null }) {
  if (current === null || previous === null) return null;
  const diff = current - previous;
  if (Math.abs(diff) < 0.005) return null;
  const cls = diff > 0 ? "delta-up" : "delta-down";
  return (
    <span className={cls}>
      {diff > 0 ? "▲" : "▼"}{Math.abs(Math.round(diff * 100))}
    </span>
  );
}

export function VersionComparison({ versions }: { versions: VersionSummary[] }) {
  // Filter BEFORE the length guard: pre-versioning calls form a NULL bucket
  // that must not count as a comparable version.
  // Oldest → newest, capped to the last 5 versions for readability.
  const ordered = versions
    .filter((v) => v.version !== null)
    .sort((a, b) => (a.version ?? 0) - (b.version ?? 0))
    .slice(-5);
  if (ordered.length < 2) {
    return (
      <div className="state-box">
        Version comparison appears once calls have been scored on at least two
        published versions.
      </div>
    );
  }
  const criterionKeys = ordered[ordered.length - 1].criteria.map((c) => c.key);

  const cell = (v: VersionSummary, key: string) =>
    v.criteria.find((c) => c.key === key)?.avg ?? null;

  return (
    <div className="version-compare-wrap">
      <table className="version-compare">
        <thead>
          <tr>
            <th>criterion</th>
            {ordered.map((v) => (
              <th key={v.version}>
                v{v.version}
                {v.count < LOW_SAMPLE && (
                  <span className="badge low-sample" title={`Only ${v.count} scored calls`}>
                    n={v.count}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="overall-row">
            <td>overall</td>
            {ordered.map((v, i) => (
              <td key={v.version} className={scoreTone(v.overallAvg)}>
                {pct(v.overallAvg)}{" "}
                <Delta current={v.overallAvg} previous={i > 0 ? ordered[i - 1].overallAvg : null} />
              </td>
            ))}
          </tr>
          {criterionKeys.map((key) => (
            <tr key={key}>
              <td>{key.replaceAll("_", " ")}</td>
              {ordered.map((v, i) => (
                <td key={v.version} className={scoreTone(cell(v, key))}>
                  {pct(cell(v, key))}{" "}
                  <Delta
                    current={cell(v, key)}
                    previous={i > 0 ? cell(ordered[i - 1], key) : null}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
